import { ForwardedRef, forwardRef } from 'react';
import { Button, ButtonProps } from '@givebutter/primitives/button';
import * as Popover from '@givebutter/primitives/popover';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePanelContext } from 'components/profile-panel/PanelContext';
import { usePanelPopoverContext } from 'components/profile-panel/PanelPopoverContext';
import { twMerge } from 'tailwind-merge';

enum ProfileItemTriggerKeys {
    Color = 'Color',
    CoverImageAdd = 'Cover Image - Add',
    CoverImageEdit = 'Cover Image - Edit',
    Email = 'Email',
    Logo = 'Logo',
    Phone = 'Phone',
    Tagline = 'Tagline',
    VerificationIcon = 'Verification Icon',
    Website = 'Website',
}

interface PanelPopoverTextButtonProps extends Omit<ButtonProps, 'variant'> {
    icon?: IconDefinition;
}
const PanelPopoverTextButton = forwardRef(
    (
        { className, children, icon, ...rest }: PanelPopoverTextButtonProps,
        forwardedRef: ForwardedRef<HTMLButtonElement>,
    ) => {
        return (
            <Button
                ref={forwardedRef}
                variant="none"
                className={twMerge(
                    '-ml-2 flex items-center gap-2 text-wrap rounded-md px-2 py-1 text-left text-sm text-gray-700 hover:bg-gray-100',
                    className,
                )}
                {...rest}
            >
                {icon && <FontAwesomeIcon icon={icon} className="size-4 shrink-0 text-gray-400" />}
                {children}
            </Button>
        );
    },
);

interface PanelPopoverIconExpandButtonProps extends Omit<ButtonProps, 'variant'> {
    icon: IconDefinition;
}
const PanelPopoverIconExpandButton = forwardRef(
    (
        { className, children, icon, ...rest }: PanelPopoverIconExpandButtonProps,
        forwardedRef: ForwardedRef<HTMLButtonElement>,
    ) => {
        const { disableExpandButtonOnHover } = usePanelContext();
        return (
            <Button
                ref={forwardedRef}
                variant="none"
                className={twMerge('group rounded-md hover:bg-gray-50', className)}
                {...rest}
            >
                <div
                    className={twMerge(
                        'group-radix-state-open:max-w-36 flex max-w-9 items-center gap-2.5 overflow-hidden p-2 transition-all duration-300 group-focus:max-w-36',
                        !disableExpandButtonOnHover && 'group-hover:max-w-36',
                    )}
                >
                    <FontAwesomeIcon
                        icon={icon}
                        className="w-5 shrink-0 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                    />
                    <span className="text-sm font-medium text-gray-600">{children}</span>
                </div>
            </Button>
        );
    },
);

interface PanelPopoverCancelButtonProps extends Popover.PopoverCloseProps {}
const PanelPopoverCancelButton = forwardRef(
    (
        { children, onClick, className, ...rest }: PanelPopoverCancelButtonProps,
        forwardedRef: ForwardedRef<HTMLButtonElement>,
    ) => {
        const { close } = usePanelPopoverContext();
        return (
            <Popover.PopoverClose
                ref={forwardedRef}
                onClick={e => {
                    close();
                    onClick?.(e);
                }}
                className={twMerge(
                    'flex h-8 rounded-md px-3 py-2.5 text-sm hover:bg-gray-100',
                    className,
                )}
                {...rest}
            >
                {children ?? 'Cancel'}
            </Popover.PopoverClose>
        );
    },
);

export {
    PanelPopoverTextButton,
    PanelPopoverIconExpandButton,
    PanelPopoverCancelButton,
    //
    ProfileItemTriggerKeys,
};
